import { Link, useLocation, useSearch } from "wouter";
import { ClipboardList, ListChecks, LogOut, UserPlus, Settings, ClipboardCheck, Stamp, Radio, Layers, Bell, GraduationCap } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
} from "@/components/ui/sidebar";
import { ThemeToggle } from "@/components/theme-toggle";
import { useAuth } from "@/hooks/use-auth";
import { useRole } from "@/hooks/use-role";
import { useViewMode } from "@/hooks/use-view-mode";
import { Starling } from "@/lib/starling-brand";
import { navIdentity, type NavGroupKey, type NavIdentity } from "@/lib/nav-identity";

// QAM brand refresh (2026-05-22, ~/Productivity/QAM/design-refresh-continuation.md):
// Poppins/Public Sans typography, branded purple identity tile, and per-group
// identity accents on the active nav item (QA blue, Active teal, Pre-Screen +
// Admin purple — see lib/nav-identity.ts). Styling is applied as inline styles
// layered over the shadcn sidebar primitive, matching the qa-detail-v3 approach.

type NavItem = { title: string; url: string; icon: typeof ListChecks };

type MatchMode = "list" | "prefix" | "exact";

const deliveryQaItems: NavItem[] = [
  { title: "List View", url: "/", icon: ListChecks },
];

// SPEC-ActiveTask Phase A — Active Tasks is its own sidebar group, between
// Delivery QA and Pre-Screen (teal identity). Live set of in-progress tasks
// (onfleetState=2) — the driver-support reference surface.
const activeTaskItems: NavItem[] = [
  { title: "Active Tasks", url: "/qa/active", icon: Radio },
];

// SPEC-PreScreening-Phase1-UI (WI-6) — Pre-Screen is its own sidebar group,
// distinct from "Delivery QA", so the pre-delivery review queue reads as a
// separate surface (purple identity) and never collides with the QA list.
const preScreenItems: NavItem[] = [
  { title: "Prescreen Queue", url: "/qa/prescreening", icon: Stamp },
];

const modelMeasurementItems: NavItem[] = [
  { title: "Named Cohorts", url: "/model/cohorts", icon: Layers },
  // Model Audit Mode — admin-gated route (AdminRoute redirects non-admins).
  { title: "Model Audit", url: "/model/audit", icon: ClipboardCheck },
];

const adminItems: NavItem[] = [
  { title: "Access Management", url: "/admin/users", icon: UserPlus },
  { title: "Annotation Review", url: "/admin/annotation-review", icon: ClipboardCheck },
  { title: "Settings", url: "/admin/settings", icon: Settings },
];

// SPEC-TrainingSimulator §7 — Training (Senior, Admin) manages the set; a
// trainee (§3, D16) sees ONLY their own runner entry pointing at /train.
const trainingItems: NavItem[] = [
  { title: "Training", url: "/train/manage", icon: GraduationCap },
];
const traineeItems: NavItem[] = [
  { title: "Training", url: "/train", icon: GraduationCap },
];

// Session 11 — all-user Preferences (per-agent toast suppression). Visible to
// every QA agent, not just admins, so non-admins can manage their own toasts.
const preferencesItems: NavItem[] = [
  { title: "Preferences", url: "/settings", icon: Bell },
];

const GROUP_LABEL_STYLE = {
  fontFamily: Starling.fontHead,
  fontWeight: 600,
  letterSpacing: "0.02em",
} as const;

function menuButtonStyle(active: boolean, id: NavIdentity) {
  if (active) {
    return {
      fontFamily: Starling.fontBody,
      fontWeight: 600,
      background: id.tintBg,
      color: id.accent,
      // left accent bar that doesn't shift the row layout
      boxShadow: `inset 3px 0 0 0 ${id.accent}`,
    };
  }
  return { fontFamily: Starling.fontBody, fontWeight: 400 };
}

export function AppSidebar() {
  const [location] = useLocation();
  const searchString = useSearch();
  const { isAdmin, isSenior, role } = useRole();
  const { logout } = useAuth();
  const isTrainee = role === "trainee";
  // SPEC-UnifiedQADetail Phase 2 — sidebar Home target follows viewMode.
  // Agent view lands on /qa/agent; senior view lands on /qa/list (with
  // preserved NQ filter params if returning from a delivery page).
  const { viewMode } = useViewMode();

  const seniorListUrl = location.startsWith("/qa/delivery/") && searchString
    ? `/qa/list?${searchString}`
    : "/qa/list";
  const listViewUrl = viewMode === "agent" ? "/qa/agent" : seniorListUrl;

  const isItemActive = (item: NavItem, mode: MatchMode): boolean => {
    if (mode === "list") {
      // Sidebar Home is "active" whenever the user is on their default list
      // surface — senior list OR agent queue, matching their viewMode.
      return (
        location === "/" ||
        location === "/qa/list" ||
        (viewMode === "agent" && location === "/qa/agent")
      );
    }
    if (mode === "exact") return location === item.url;
    return location === item.url || location.startsWith(item.url);
  };

  const renderGroup = (
    label: string,
    items: NavItem[],
    identityKey: NavGroupKey,
    mode: MatchMode,
  ) => {
    const id = navIdentity(identityKey);
    return (
      <SidebarGroup>
        <SidebarGroupLabel style={GROUP_LABEL_STYLE}>{label}</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {items.map((item) => {
              const active = isItemActive(item, mode);
              const href = item.title === "List View" ? listViewUrl : item.url;
              return (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={active}
                    style={menuButtonStyle(active, id)}
                    data-testid={`nav-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Link href={href}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  };

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-md"
            style={{ background: Starling.purple }}
          >
            <ClipboardList className="h-5 w-5" style={{ color: "#FFFFFF" }} />
          </div>
          <div className="flex flex-col">
            <span style={{ fontFamily: Starling.fontHead, fontWeight: 600, fontSize: 14, color: Starling.body }}>
              Starling
            </span>
            <span style={{ fontFamily: Starling.fontBody, fontSize: 12, color: Starling.bodyMuted }}>
              Delivery QA
            </span>
          </div>
        </div>
        {/* Theme and Sign out, pinned under the lockup (the header only scrolls the nav below). */}
        <div className="flex items-center justify-between gap-2" data-testid="sidebar-account-actions">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => logout()}
            className="flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            style={{ fontFamily: Starling.fontBody, fontSize: 12, fontWeight: 500 }}
            data-testid="button-logout-sidebar"
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
            Sign out
          </button>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {isTrainee ? renderGroup("Training", traineeItems, "training", "prefix") : (<>
        {renderGroup("Delivery QA", deliveryQaItems, "delivery-qa", "list")}
        {renderGroup("Active Tasks", activeTaskItems, "active-tasks", "prefix")}
        {renderGroup("Pre-Screen", preScreenItems, "pre-screen", "prefix")}
        {renderGroup("Model Measurement", modelMeasurementItems, "model-measurement", "prefix")}
        {(isSenior || isAdmin) && renderGroup("Training", trainingItems, "training", "prefix")}
        {isAdmin && renderGroup("Admin", adminItems, "admin", "exact")}
        {renderGroup("Preferences", preferencesItems, "admin", "exact")}
        </>)}
      </SidebarContent>
    </Sidebar>
  );
}
